import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { InvoicesService } from '@cl/core/services/invoices.service';
import { SinvoicesService } from '@cl/core/services/sinvoices.service';
import { DATE_FORMAT } from '@cl/shared/constants';
import { DepartmentPermissionsType } from '@cl/shared/department-permissions-type.enum';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { CommandResult, EntityType } from '@cl/shared/models';
import { Invoice } from '@cl/shared/models/invoices/invoice.model';
import { Sinvoice } from '@cl/shared/models/sinvoices/sinvoice.model';
import { SumPipe } from '@cl/shared/pipes/sum.pipe';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'cl-sinvoice-invoices-table',
    standalone: false,
    templateUrl: './sinvoice-invoices-table.component.html',
    styleUrl: './sinvoice-invoices-table.component.less',
})
export class SinvoiceInvoicesTableComponent {
    GroupPermission = GroupPermissionsType;
    DepartmentPermission = DepartmentPermissionsType;
    EntityType = EntityType;
    DATE_FORMAT = DATE_FORMAT;

    @Input() sinvoice: Sinvoice;
    @Output() sinvoiceChanged: EventEmitter<void> = new EventEmitter<void>();

    loading: boolean = false;
    isAutomaticCalculationsEnabled: boolean = true;
    toAddInvoice: Invoice;

    sumFormatter = (value: number): string => {
        return `${new SumPipe().transform(value)}`;
    };

    searchInvoicesForSinvoice = async (searchText: string): Promise<CommandResult<Invoice[]>> => {
        if (!this.sinvoice) {
            return new Promise<CommandResult<Invoice[]>>(null);
        }

        return await lastValueFrom(this.invoicesService.searchInvoicesForSinvoice(this.sinvoice.id, searchText));
    };

    constructor(
        private messageService: NzMessageService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private sinvoicesService: SinvoicesService,
        private invoicesService: InvoicesService,
        private modalService: NzModalService,
    ) {}

    openInvoice(invoiceId: number): void {
        this.router.navigate(['base-data', 'invoices', 'view-invoice', invoiceId]);
    }

    onSumVatChange(invoice: Invoice): void {
        if (!this.isAutomaticCalculationsEnabled) {
            return;
        }
        this.cdr.detectChanges();

        if (invoice.vatPercent == 0 && invoice.sumVat > 0) {
            this.messageService.warning('Izvēlētā PVN procentu likme ir 0%, PVN summa nevar būt lielāka par 0 EUR!');
            invoice.sumVat = 0;
            invoice.sumWithVat = invoice.sumWithoutVat + invoice.penaltySumWithoutVat;
            return;
        }

        invoice.sumWithoutVat = Number(((invoice.sumVat * 100) / invoice.vatPercent).toFixed(2));
        invoice.sumWithVat = Number((invoice.sumWithoutVat + invoice.sumVat + invoice.penaltySumWithoutVat).toFixed(2));
    }

    onSumWithVatChange(invoice: Invoice): void {
        if (!this.isAutomaticCalculationsEnabled) {
            return;
        }

        if (invoice.vatPercent == 0) {
            invoice.sumWithoutVat = Number((invoice.sumWithVat - invoice.penaltySumWithoutVat).toFixed(2));
            invoice.sumVat = 0;
            return;
        }

        invoice.sumWithoutVat = Number(((invoice.sumWithVat - invoice.penaltySumWithoutVat) / (1 + invoice.vatPercent / 100)).toFixed(2));
        invoice.sumVat = Number((invoice.sumWithVat - invoice.penaltySumWithoutVat - invoice.sumWithoutVat).toFixed(2));
    }

    recalculateAllSums(invoice: Invoice): void {
        if (!this.isAutomaticCalculationsEnabled) {
            return;
        }

        invoice.sumVat = Number((invoice.sumWithoutVat * (invoice.vatPercent / 100)).toFixed(2));
        invoice.sumWithVat = Number((invoice.sumWithoutVat + invoice.sumVat + invoice.penaltySumWithoutVat).toFixed(2));
    }

    onIsAutomaticCalculationsEnabledChanged(isAutomaticCalculationsEnabled: boolean): void {
        if (!isAutomaticCalculationsEnabled) {
            return;
        }

        this.sinvoice.invoices.forEach((i: Invoice): void => {
            this.recalculateAllSums(i);
        });
    }

    async addInvoiceToSinvoice(): Promise<void> {
        if (!this.toAddInvoice) {
            return;
        }

        try {
            if (this.toAddInvoice.sinvoiceId) {
                await this.modalService.confirm({
                    nzTitle: 'Rēķins jau ir pievienots citam virsrēķinam. Vai turpināt?',
                    nzCancelText: 'Atcelt',
                    nzOnOk: async (): Promise<void> => {
                        await this.handleInvoiceAddToSinvoice(this.toAddInvoice.id);
                        return;
                    },
                    nzOnCancel: async (): Promise<void> => {
                        return;
                    },
                });
                return;
            }

            await this.handleInvoiceAddToSinvoice(this.toAddInvoice.id);
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda pievienojot rēķinu');
        }
    }

    async removeInvoiceFromSinvoice(invoiceId: number): Promise<void> {
        try {
            this.loading = true;
            await this.sinvoicesService.removeInvoiceFromSinvoice(this.sinvoice.id, invoiceId);
            this.sinvoiceChanged.emit();
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda atsaistot rēķinu no virsrēķina');
            return;
        }
    }

    async handleInvoiceAddToSinvoice(invoiceId: number): Promise<void> {
        try {
            this.loading = true;
            await this.sinvoicesService.addInvoiceToSinvoice(this.sinvoice.id, invoiceId);
            this.sinvoiceChanged.emit();
            this.toAddInvoice = null;
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda piesaistot rēķinu virsrēķinam');
            return;
        }
    }
}
